/**
 * Tâches 1288 · 1289 (Eric 2026-06-30) — Vérifier le courriel du lundi.
 *
 * « Relire » compose le courriel et l'affiche tel quel, sans rien envoyer :
 * accessible à qui peut voir cet écran. « M'envoyer l'aperçu maintenant »
 * exerce la VRAIE chaîne d'envoi et reste réservé au fondateur.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import GapsDigestPreviewModal from "./GapsDigestPreviewModal";

export const GapsDigestPreviewButton = () => {
  const { user } = useAuth();
  const { tr } = useLang();
  const [sending, setSending] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isFounder = !!(user?.is_founder || user?.real_is_founder);

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/coverage-gaps/weekly-digest/preview`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      const resume = tr(
        `${data.items} vidéo(s), ${data.total_missing_min} min manquantes`,
        `${data.items} video(s), ${data.total_missing_min} min missing`,
      );
      if (data.delivery !== "sent") {
        // Tâche 1227 — Le pod de préproduction bloque les envois : le dire, et
        // dire LEQUEL des deux garde-fous a bloqué, plutôt qu'annoncer un
        // courriel qui n'est jamais parti.
        toast.warning(
          tr(
            `Courriel composé (${resume}) mais NON livré — ${data.blocked_by || data.delivery}.`,
            `Email composed (${resume}) but NOT delivered — ${data.blocked_by || data.delivery}.`,
          ),
          { duration: 14000 },
        );
      } else {
        toast.success(tr(
          `Aperçu envoyé à ${data.to} — ${resume}.`,
          `Preview sent to ${data.to} — ${resume}.`,
        ), { duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Envoi impossible", "Could not send"), { duration: 10000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
      data-testid="gaps-digest-preview"
    >
      <span>
        <span className="block text-sm font-medium text-zinc-900">
          {tr("Vérifier le courriel du lundi, maintenant", "Check Monday's email right now")}
        </span>
        <span className="block text-xs text-zinc-500">
          {tr(
            "« Relire » l'affiche tel qu'il partira, sans rien envoyer.",
            "“Read it” shows the email exactly as it will go out, without sending anything.",
          )}
          {isFounder
            ? tr(
                " L'envoi exerce la vraie chaîne, vers ton adresse, et n'entame pas l'anti-doublon du lundi.",
                " Sending exercises the real chain, to your own address, and does not consume Monday's duplicate guard.",
              )
            : ""}
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-2 self-start flex-shrink-0">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-semibold"
          data-testid="gaps-digest-render-btn"
        >
          👁 {tr("Relire le courriel", "Read the email")}
        </button>
        {isFounder && (
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="px-3 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-50"
            data-testid="gaps-digest-preview-btn"
          >
            {sending ? tr("⏳ Envoi…", "⏳ Sending…") : tr("✉ M'envoyer l'aperçu maintenant", "✉ Send me the preview now")}
          </button>
        )}
      </span>
      <GapsDigestPreviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        canSend={isFounder}
      />
    </div>
  );
};

export default GapsDigestPreviewButton;
